import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import ClearIcon from "@mui/icons-material/Clear";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import useOrganisationStorage from "lib/storage/useOrganisationStorage";
import { StorageEntry } from "lib/storage/useStorage";

import { asTitle } from "utils/Utils";

import { BlueprintType } from "core/blueprint/base/BlueprintService";
import { getBlueprintTypeString } from "core/blueprint/base/Utils";

// TODO: move to a more generic file.
export type Filter = {
	[key: string]: string;
};

export type BlueprintFilterProps = {
	onChange: (filter: Filter) => void;
};

const EMPTY_FILTER: Filter = {
	type: "",
	name: "",
	description: ""
};

const filterEntry: StorageEntry = {
	key: "blueprints.filter"
};

const BlueprintFilter: React.FC<BlueprintFilterProps> = (
	props: BlueprintFilterProps
) => {
	const { t } = useTranslation();

	const filterCookie = useOrganisationStorage(filterEntry);

	const [expanded, setExpanded] = useState(true);
	const [filter, setFilter] = useState(EMPTY_FILTER);

	useEffect(() => {
		if (filterCookie.value == null) {
			setFilter(EMPTY_FILTER);
			return;
		}

		const currentValue = JSON.stringify(filter);
		if (filterCookie.value === currentValue) {
			return;
		}

		setFilter(JSON.parse(filterCookie.value));
	}, [filterCookie.value]);

	useEffect(() => {
		props.onChange(filter);
	}, [filter, props.onChange]);

	function setFilterValue(key: string, value: string): void {
		const newFilter = {
			...filter,
			[key]: value
		};
		filterCookie.set(JSON.stringify(newFilter));
	}

	return (
		<Box
			sx={{
				marginTop: 2,
				marginBottom: 2,
				paddingRight: 1,
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				backgroundColor: "background.paper"
			}}>
			<Grid container alignItems={"center"}>
				<Grid item>
					<IconButton onClick={() => setExpanded(!expanded)}>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</Grid>
				<Grid item flexGrow={1}>
					<Typography variant="subtitle1">
						{asTitle(t("filter"))}
					</Typography>
				</Grid>
				<Grid item>
					<Button
						variant="outlined"
						size="small"
						startIcon={<ClearIcon />}
						onClick={filterCookie.remove}>
						{t("clear")}
					</Button>
				</Grid>
			</Grid>
			<Collapse
				in={expanded}
				timeout="auto"
				sx={{ paddingLeft: 2, paddingRight: 2 }}>
				<Grid container alignItems="center">
					<Grid item xs={4} sm={3} md={2} lg={1.5}>
						<Typography variant="subtitle2">
							{asTitle(t("type"))}
						</Typography>
					</Grid>
					<Grid item xs={8} sm={6} md={4} lg={4}>
						<TextField
							select
							fullWidth
							size="small"
							variant="standard"
							value={filter.type}
							onChange={e =>
								setFilterValue("type", e.target.value)
							}>
							<MenuItem value="">
								<p style={{ margin: 12 }} />
							</MenuItem>
							{Object.values(BlueprintType).map(type => {
								return (
									<MenuItem key={type} value={type}>
										{t(getBlueprintTypeString(type))}
									</MenuItem>
								);
							})}
						</TextField>
					</Grid>
				</Grid>
				<Grid container alignItems="center">
					<Grid item xs={4} sm={3} md={2} lg={1.5}>
						<Typography variant="subtitle2">
							{asTitle(t("name"))}
						</Typography>
					</Grid>
					<Grid item xs={8} sm={6} md={4} lg={4}>
						<TextField
							fullWidth
							size="small"
							variant="standard"
							value={filter.name}
							onChange={e =>
								setFilterValue("name", e.target.value)
							}
						/>
					</Grid>
				</Grid>
				<Grid container alignItems="center" sx={{ paddingBottom: 2 }}>
					<Grid item xs={4} sm={3} md={2} lg={1.5}>
						<Typography variant="subtitle2">
							{asTitle(t("description"))}
						</Typography>
					</Grid>
					<Grid item xs={8} sm={6} md={4} lg={4}>
						<TextField
							fullWidth
							size="small"
							variant="standard"
							value={filter.description}
							onChange={e =>
								setFilterValue("description", e.target.value)
							}
						/>
					</Grid>
				</Grid>
			</Collapse>
		</Box>
	);
};

export default BlueprintFilter;
